// TODO: Consider loading the rules from the backend (/misc/validate.php) so we can reuse the same patterns and error messages for both client and server side validation.

/**
 * @typedef {Object} ValidationRule
 * @property {string} pattern - The validation pattern to test against
 * @property {string} [de] - The message to show if validation fails in German
 * @property {string} [da] - The message to show if validation fails in Danish
 * @property {string} [fi] - The message to show if validation fails in Finnish
 * @property {string} [fr] - The message to show if validation fails in French
 * @property {string} [nl] - The message to show if validation fails in Dutch
 * @property {string} [sv] - The message to show if validation fails in Swedish
 */

/**
 * @typedef {Object} ValidationError
 * @property {string} error - The type of error that occurred
 * @property {string} pattern
 * @property {string} invalidMessage
 */

export const formValidation = {

    /**
     * Injects validation rules into an input element if they for some reason can not be set in the markup. i.e. in the cms where the tags are stripped away.
     * @param {boolean} required - Controlls the aria-required and required attributes
     * @param {ValidationRule[]} ruleSet - The validation rules to inject
     * @param {HTMLInputElement} input - The input element to inject the rules into
     * @param {string} languageIsoCode - The language iso code to use for the validation messages
     * @returns {void}
     */
    injectDataTag: (required, ruleSet, input, languageIsoCode) => {
        if (required) {
            input.setAttribute('aria-required', 'true');
            input.required = true;
        }
        input.dataset.validationRules = formValidation.rulesetToString(ruleSet, languageIsoCode);
    },

    /**
     * Converts an array of validation rules into a string
     * @param {ValidationRule[]} ruleset - The validation rules to convert
     * @param {string} languageIsoCode - The language iso code to use for the validation messages
     * @returns {string} The string representation of the validation rules
     * @throws {Error} If ruleSet is invalid.
     */
    rulesetToString: (ruleset, languageIsoCode) => {
        if (!Array.isArray(ruleset)) {
            throw new Error("form-validation.js/rulesetToString: ruleSet is not an array");
        }
        if (!languageIsoCode) {
            throw new Error("form-validation.js/rulesetToString: languageIsoCode is required");
        }
        var result = '';

        ruleset.forEach(rule => {
            if (!rule.pattern) {
                throw new Error("form-validation.js/rulesetToString: rule is missing pattern or invalidMessage");
            }
            if (!rule[languageIsoCode]) {
                const message = `form-validation.js/rulesetToString: rule ('${JSON.stringify(rule)}') is missing invalidMessage for language ${languageIsoCode}`;
                throw new Error(message);
            }
            result += `${rule.pattern}§${rule[languageIsoCode]}§`;
        });
        return result;
    },

    /**
     * Handles form validation and submission using regex patterns and custom error messages.
     * 
     * @param {HTMLFormElement} form - The form element to validate
     * @param {function(SubmitEvent): void} submitAction - Called with submit event if validation passes
     * @returns {void}
     * @throws {Error} If form is missing or submitAction is not a function
     * 
     * Required HTML attributes:
     * - data-validation-rules: Regex patterns and error messages separated by § character
     *   Format: "pattern§error_message[§pattern§error_message]..."
     * 
     * Optional HTML attributes:  
     * - aria-required="true": Makes the field required
     * - required: Native HTML required attribute
     * 
     * @example HTML
     * <form>
     *   <input type="email" 
     *     required
     *     aria-required="true"
     *     data-validation-rules="^.+@.+\\..+$§Invalid email format"
     *   />
     *   <div class="error-message"></div>
     *   <button type="submit">Submit</button>
     * </form>
     * 
     * @example Validation Rules
     * // Company name validation
     * "^\\S{1,}$§Name is required§^.{1,150}$§Max 150 chars§^\\D§Cannot start with number"
     * // Email validation
     * "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,256}$§Invalid email"
     * 
     * Note: Rules are checked in order, first failed pattern's message is shown
     * Note: All failed rules are available if we want to show them all at once.
     */
    addListeners: (form, submitAction) => {
        if (typeof submitAction !== 'function') {
            throw new Error("form-validation.js/addListeners: submitAction is not a function");
        }
        if (!form) {
            throw new Error("form-validation.js/addListeners: form is missing");
        }
        const inputs = form.querySelectorAll('input[data-validation-rules],textarea[data-validation-rules]');

        const isValid = (input, inputErrorDiv, rules) => {
            if (!input.dataset.validationActive || input.dataset.validationActive === "false") {
                // input.validity.valid = true; // getter only. Should we handle this some other way?
                return true; // fields not active are handled as valid
            }
            const { failedRules } = formValidation.testRuleSet(rules, input.value);
            const isValid = failedRules.length === 0;
            input.classList.toggle('error', !isValid);
            input.classList.toggle('valid', isValid);
            input.setAttribute('aria-invalid', !isValid);
            // input.validity.valid = isValid; // getter only. Should we handle this some other way?
            inputErrorDiv.textContent = isValid ? '' : failedRules[0].invalidMessage;
            return isValid;
        };

        inputs.forEach(input => {
            if (!input.dataset.validationActive) {
                input.dataset.validationActive = "false";
            }
            const inputErrorDiv = input.nextElementSibling;
            if (!inputErrorDiv) {
                throw new Error("form-validation.js/validationListener: inputErrorDiv not found", input);
            }
            if (!inputErrorDiv.classList.contains('error-message')) {
                throw new Error("form-validation.js/validationListener: inputErrorDiv is missing class 'error-message'", inputErrorDiv);
            }
            const { rules, parsingErrors } = formValidation.rulesetFromString(input.dataset.validationRules);
            if (parsingErrors.length > 0) {
                throw new Error("form-validation.js/validationListener: parsing errors", parsingErrors);
            }
            // after change we start validating the input on every input or blur event
            input.addEventListener('change', () => input.dataset.validationActive = true);
            input.addEventListener('input', () => isValid(input, inputErrorDiv, rules));
            input.addEventListener('blur', () => isValid(input, inputErrorDiv, rules));
        });

        form.addEventListener('submit', (event) => {
            event.preventDefault();
            event.stopPropagation();
            let willPost = true;
            inputs.forEach(input => {
                const inputErrorDiv = input.nextElementSibling;
                if (!inputErrorDiv) {
                    console.error("form-validation.js/validationListener: inputErrorDiv not found", input);
                }
                if (!inputErrorDiv.classList.contains('error-message')) {
                    console.error("form-validation.js/validationListener: inputErrorDiv is missing class 'error-message'", inputErrorDiv);
                }
                const { rules, parsingErrors } = formValidation.rulesetFromString(input.dataset.validationRules);
                if (parsingErrors.length > 0) {
                    console.error("form-validation.js/validationListener: parsing errors, ", parsingErrors);
                }

                // on submit we validate all fields
                input.dataset.validationActive = true;
                if (!isValid(input, inputErrorDiv, rules)) {
                    willPost = false;
                }
            });
            if (willPost) {
                submitAction(event);
            }
        });
    },

    textAreaCounter: (textarea, infoMessage, max) => {
        if (!textarea) {
            throw new Error("form-validation.js/textAreaCounter: textarea is missing");
        }
        if (!infoMessage) {
            throw new Error("form-validation.js/textAreaCounter: infoMessage is missing");
        }
        if (typeof max !== 'number' || max <= 0) {
            throw new Error("form-validation.js/textAreaCounter: max must be a number and greater than 0");
        }
        infoMessage.textContent = textarea.value.length + "/" + max;
        textarea.addEventListener("input", () => {
            infoMessage.textContent = textarea.value.length + "/" + max;
        });
        textarea.setAttribute("maxlength", max);
    },

    /**
     * Parses a string of validation rules into an array of validation rules.
     * Separator: `§` (paragraph mark)
     * Format: `pattern§error_message[§pattern§error_message]...`
     * 
     * @param {string} validationString - The string containing validation rules
     * @returns {{rules: ValidationRule[], parsingErrors: ValidationError[]}} Object containing parsed rules and any parsing errors
     *
     * @example
     * const { rules, parsingErrors } = formValidation.rulesetFromString(validationString);
     * if (parsingErrors.length > 0) { 
     *     console.error("Parsing errors:", parsingErrors);
     *     return { rules: [], parsingErrors };
     * }
     * 
     * TODO: add support for more browser native input.validity values.
     */
    rulesetFromString: (validationString) => {
        let rules = [];
        let parsingErrors = [];

        if (!validationString || typeof validationString !== 'string') {
            parsingErrors.push(`Bad validation string provided: got '${validationString}', wanted a string`);
            return { rules, parsingErrors };
        }

        const segments = validationString.split('§');
        const parseSegments = (segments, rules = [], parsingErrors = []) => {
            // Base case - no more segments
            if (segments.length === 0 ||
                segments.length === 1 && segments[0].trim() === '') {
                return { rules, parsingErrors };
            }

            // Base case - one more segment
            if (segments.length === 1 && segments[0].trim() !== '') {
                parsingErrors.push('Validation string has an unpaired segment');
                parsingErrors.push(`Remaining unparsed text: "${segments[0]}"`);
            }

            const [pattern, message, ...rest] = segments;

            // Try to validate pattern is a regex
            try {
                new RegExp(pattern);
            } catch (e) {
                parsingErrors.push(`Invalid regex pattern: "${pattern}"`);
                if (pattern.startsWith('^') || pattern.endsWith('$')) {
                    parsingErrors.push('Note: Pattern looks like regex but is invalid');
                }
                return { rules, parsingErrors };
            }

            rules.push({
                pattern,
                invalidMessage: message
            });

            // Recursive case - process remaining segments
            return parseSegments(rest, rules, parsingErrors);
        };

        return parseSegments(segments);
    },
    /**
     * Tests a value against a set of validation rules
     * @param {Array<ValidationRule>} ruleSet - Array of rules, each containing a pattern and invalidMessage
     * @param {string} value - The value to test against the rules
     * @returns {{failedRules: string[], matchedRules: ValidationRule[]}} Object containing failed rules and matched rules
     * @example destructuring result
     * const { failed, matched } = formValidation.testRuleSet(ruleSet, '123');
     */
    testRuleSet: (ruleSet, value) => {
        let failedRules = [];
        let matchedRules = [];
        ruleSet.forEach(rule => {
            if (value.trim().match(new RegExp(rule.pattern))) {
                matchedRules.push(rule);
            } else {
                failedRules.push(rule);
            }
        });
        return { failedRules, matchedRules };
    },


};


// TODO: Check rules and the order of rules so the first failed rule is the one that is the most relevant.

export const rules = {
    companyName: [
        {
            pattern: '\\S',
            de: 'Firmenname ist erforderlich',
            da: 'Virksomhedsnavn er påkrævet',
            fi: 'Yrityksen nimi on pakollinen',
            fr: 'Le nom de l\'entreprise est requis',
            nl: 'Bedrijfsnaam is verplicht',
            sv: 'Företagets namn är obligatoriskt',
            en: 'Company name is required'
        },
        {
            pattern: '^.{1,150}$',
            de: 'Der Firmenname muss zwischen 1 und 150 Zeichen lang sein',
            da: 'Virksomhedsnavnet skal være mellem 1 og 150 tegn',
            fi: 'Yrityksen nimen tulee olla 1-150 merkkiä',
            fr: 'Le nom de l\'entreprise doit comporter entre 1 et 150 caractères',
            nl: 'Bedrijfsnaam moet tussen 1 en 150 tekens lang zijn',
            sv: 'Företagsnamnet måste vara mellan 1 och 150 tecken',
            en: 'Company name must be between 1 and 150 characters'
        },
    ],
    orgNumber: [
        {
            pattern: '\\S',
            de: 'Organisationsnummer ist erforderlich',
            da: 'Organisationsnummer er påkrævet',
            fi: 'Organisaatiotunnus on pakollinen',
            fr: 'Le numéro d\'organisation est requis',
            nl: 'Organisatiecode is vereist',
            sv: 'Organisationsnummer är obligatoriskt',
            en: 'Organization number is required',
        },
        {
            pattern: '^[a-zA-Z0-9\\-]{6,32}$',
            de: "Falsches Format",
            da: "Forkert format",
            fi: "Väärä muoto",
            fr: "Format incorrect",
            nl: "Onjuist formaat",
            sv: "Ej korrekt format",
            en: "Incorrect format",
        }
    ],
    name: [
        {
            pattern: '\\S',
            de: 'Name ist erforderlich',
            da: 'Navn er påkrævet',
            fi: 'Nimi on pakollinen',
            fr: 'Le nom est requis',
            nl: 'Naam is verplicht',
            sv: 'Namnet är obligatoriskt',
            en: 'Name is required'
        }
    ],
    email: [
        {
            pattern: '@',
            de: 'E-Mail-Adresse muss ein @ enthalten',
            da: 'E-Mail-adresse skal indeholde et @',
            fi: 'Sähköpostiosoite sisältää @ merkinnän',
            fr: 'L\'adresse e-mail doit contenir un @',
            nl: 'E-mailadres moet een @ bevatten',
            sv: 'E-postadressen måste innehålla ett @',
            en: 'E-mail address must contain an @'
        },
        {
            pattern: '^[\\S ]{1,64}@',
            de: 'Der erste Teil der E-Mail-Adresse muss zwischen 1 und 64 Zeichen lang sein',
            da: 'Den første del af e-mailadressen skal være mellem 1 og 64 tegn',
            fi: 'Sähköpostiosoitteen ensimmäisen osan tulee olla 1-64 merkkiä',
            fr: 'La première partie de l\'adresse e-mail doit comporter entre 1 et 64 caractères',
            nl: 'Het eerste deel van het e-mailadres moet tussen 1 en 64 tekens lang zijn',
            sv: 'Den första delen av e-postadressen måste vara mellan 1 och 64 tecken',
            en: 'The first part of the e-mail address must be between 1 and 64 characters'
        },
        {
            pattern: '[^@]$',
            de: 'Darf nicht mit @ enden',
            da: 'Må ikke slutte med @',
            fi: 'Ei voi päättyä @-merkkiin',
            fr: 'Ne peut pas se terminer par @',
            nl: 'Mag niet eindigen met @',
            sv: 'Får inte sluta med @',
            en: 'Can not end with @'
        },
        {
            pattern: '^(?:[^@]|"[^"]*")*@(?:[^@]|"[^"]*")*$',
            de: 'E-Mail-Adresse darf nicht mehr als ein @ enthalten',
            da: 'E-mailadresse må ikke indeholde mere end ét @',
            fi: 'Sähköpostiosoite ei saa sisältää enempää kuin yhden @-merkin',
            fr: 'L\'adresse e-mail ne peut pas contenir plus d\'un @',
            nl: 'E-mailadres mag niet meer dan één @ bevatten',
            sv: 'E-postadressen får inte innehålla mer än ett @',
            en: 'Email address cannot contain more than one @'
        },
        {
            pattern: '@[^_\\n]+$',
            de: 'Unterstrich ist im Domain-Teil nicht erlaubt',
            da: 'Understregning er ikke tilladt i domænedelen',
            fi: 'Alaviiva ei ole sallittu verkkotunnusosassa',
            fr: 'Le trait de soulignement n\'est pas autorisé dans la partie domaine',
            nl: 'Underscore is niet toegestaan in het domeingedeelte',
            sv: 'Understreck är inte tillåtet i domändelen',
            en: 'Underscore is not allowed in the domain part'
        },
        {
            pattern: '@(?:[a-zA-Z0-9\\-\\.]+(?:\.[a-zA-Z0-9-]+)*)$|(?:\\[IPv6(?::[0-9a-f]{1,4}){8}\\])|(?:\\[\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\])$',
            de: 'Der Domain-Teil der E-Mail-Adresse enthält ungültige Zeichen',
            da: 'Domænedelen af e-mailadressen indeholder ugyldige tegn',
            fi: 'Sähköpostiosoitteen verkkotunnusosa sisältää virheellisiä merkkejä',
            fr: 'La partie domaine de l\'adresse e-mail contient des caractères non valides',
            nl: 'Het domeingedeelte van het e-mailadres bevat ongeldige tekens',
            sv: 'Domändelen av e-postadressen innehåller ogiltiga tecken',
            en: 'The domain part of the email address contains invalid characters'
        },
        {
            pattern: '^[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~]+|(?:"[a-zA-Z0-9 "(),?!:;<>@\\[\\\\\\]#$%&\'*+-/=^_`{|}~]+")',
            de: 'Ungültiges Zeichen im ersten Teil der E-Mail-Adresse',
            da: 'Ugyldigt tegn i første del af e-mailadressen',
            fi: 'Virheellinen merkki sähköpostiosoitteen ensimmäisessä osassa',
            fr: 'Caractère invalide dans la première partie de l\'adresse e-mail',
            nl: 'Ongeldig teken in het eerste deel van het e-mailadres',
            sv: 'Ogiltigt tecken i första delen av e-postadressen',
            en: 'Invalid character in first part of email address'
        },
        {
            pattern: '(?:^".+"@)|(?:"[^"]+"[\\.@])|^[^"]+$',
            de: 'Zitierte Teile müssen durch Punkte getrennt sein oder den gesamten lokalen Teil ausmachen',
            da: 'Citerede dele skal adskilles af punktummer eller udgøre hele den lokale del',
            fi: 'Lainausmerkeillä merkittyjen osien tulee olla erotettu pisteillä tai muodostaa koko paikallinen osa',
            fr: 'Les parties entre guillemets doivent être séparées par des points ou constituer l\'intégralité de la partie locale',
            nl: 'Geciteerde delen moeten gescheiden zijn door punten of het hele lokale deel vormen',
            sv: 'Citerade delar måste separeras med punkter eller utgöra hela den lokala delen',
            en: 'Quoted parts must be separated by dots or make up the entire local part'
        },
    ],
    phone: [
        {
            pattern: '^[+(0-9]',
            de: 'Telefonnummer muss mit einer Ziffer, + oder ( beginnen',
            da: 'Telefonnummeret skal starte med et tal, + eller (',
            fi: 'Puhelinnumeron tulee alkaa numerolla, + tai (',
            fr: 'Le numéro de téléphone doit commencer par un chiffre, + ou (',
            nl: 'Telefoonnummer moet beginnen met een cijfer, + of (',
            sv: 'Telefonnumret måste börja en siffra, + eller (',
            en: 'Phone number must start with a digit, + or ('
        },
        {
            pattern: '^[0-9+\\- \\(\\)]*$',
            de: 'Telefonnummer darf nur Ziffern, +, -, (), und Leerzeichen enthalten',
            da: 'Telefonnummeret må kun indeholde tal, +, -, (), og mellemrum',
            fi: 'Puhelinnumero saa sisältää vain numeroita, +, -, (), ja välilyöntejä',
            fr: 'Le numéro de téléphone ne peut contenir que des chiffres, +, -, (), et des espaces',
            nl: 'Telefoonnummer mag alleen cijfers, +, -, (), en spaties bevatten',
            sv: 'Telefonnumret får bara innehålla siffror, +, -, (), och mellanslag',
            en: 'Phone number may only contain digits, +, -, (), and spaces'
        },
        {
            pattern: '^(?:[^0-9]*[0-9]){6,26}[^0-9]*$',
            de: 'Telefonnummer muss zwischen 6 und 26 Ziffern enthalten',
            da: 'Telefonnummeret skal indeholde mellem 6 og 26 cifre',
            fi: 'Puhelinnumerossa on oltava 6-26 numeroa',
            fr: 'Le numéro de téléphone doit contenir entre 6 et 26 chiffres',
            nl: 'Telefoonnummer moet tussen 6 en 26 cijfers bevatten',
            sv: 'Telefonnumret måste innehålla mellan 6 och 26 siffror',
            en: 'Phone number must contain between 6 and 26 digits'
        }
    ],
    numberPositive: [
        {
            pattern: '^[0-9 ]*$',
            de: 'Das Feld darf nur Zahlen enthalten',
            da: 'Feltet kan kun indeholde tal',
            fi: 'Kenttä voi sisältää vain numeroita',
            fr: 'Le champ ne peut contenir que des chiffres',
            nl: 'Het veld kan alleen cijfers bevatten',
            sv: 'Fältet kan bara innehålla siffror',
            en: 'The field can only contain numbers'
        }
    ],
}